import { Component, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { FormBuilder, FormArray, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ApiCallService } from "../../../../api/service/api-call.service";
import * as Constants from "../../../../constant/Constants";
import { ValidationService } from "src/app/services/validation.service";
import { IConfirmDialogData } from "src/app/components/dialog/dialog.data";
import { ConfirmComponent } from "src/app/components/dialog/confirm/confirm.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-dp-claim",
  templateUrl: "./claim.component.html",
  styleUrls: ["./claim.component.css"],
})
export class DpClaimComponent implements OnInit {
  token: string = "placeholder-value";
  loading: boolean = false;
  submitted: boolean = false;
  claimNo: string = "";
  url: string = environment.baseUrl.replace("/motor", "") + "/dp-chat" + Constants.dpClaimURL;

  policyNo: string = "";
  insuredName: string = "";

  lossTypeOptions: string[] = ["Fire", "Theft or Burglary", "All Risks"];

  genericDialogData: IConfirmDialogData = {
    title: "",
    content: "",
    cancelButtonText: "",
    confirmButtonText: "",
  };

  dpForm = this._formBuilder.group({
    lossDate: ["", Validators.required],
    lossTime: [""],
    lossType: ["", Validators.required],
    lossDescription: ["", Validators.required],
    claimItems: this._formBuilder.array([]),
    theftDetails: this._formBuilder.group({
      discoveryDate: [""],
      discoveryTime: [""],
      discoveredBy: [""],
      lastSeenDate: [""],
      lastSeenTime: [""],
      lastSeenBy: [""],
      policeNotifiedDate: [""],
      policeStationAddress: [""],
      recoverySteps: [""],
      totalContentsValue: [""],
      exteriorDoorsSecurity: [""],
      interiorDoorsSecurity: [""],
      windowsSecurity: [""],
      furtherAntiTheftMeasures: [""],
      securityGuardOnDuty: [""],
      securityCompanyName: [""],
      premisesUnoccupied: [""],
    }),
  });

  formErrors: { [key: string]: string } = {
    lossDate: "",
    lossType: "",
    lossDescription: "",
  };

  validationMessages: { [key: string]: { [key: string]: string } } = {
    lossDate: { required: "This field is required" },
    lossType: { required: "This field is required" },
    lossDescription: { required: "This field is required" },
    itemDescription: { required: "This field is required" },
  };

  constructor(
    private route: ActivatedRoute,
    private apiCallService: ApiCallService,
    private _formBuilder: FormBuilder,
    private validationService: ValidationService,
    public dialog: MatDialog,
    private titleService: Title
  ) {}

  get claimItems(): FormArray {
    return this.dpForm.get("claimItems") as FormArray;
  }

  get showTheftDetails(): boolean {
    const lossType = this.dpForm.get("lossType")?.value;
    return !!lossType && lossType !== "Fire";
  }

  ngOnInit(): void {
    this.titleService.setTitle("Claim Insurance Application");

    this.route.queryParams.subscribe((params) => {
      if (params["token"] && params["token"] !== "undefined") {
        this.token = params["token"];
        this.loadPrefill();
      }
    });

    this.dpForm.valueChanges.subscribe(() => this.logErrors());
  }

  async loadPrefill(): Promise<void> {
    try {
      const response = await this.apiCallService.getRequest(`${this.url}?token=${this.token}`);
      if (response && response.error === false && response.prefill) {
        const p = response.prefill;
        this.policyNo = p.PolicyNo || "";
        this.insuredName = p.InsuredName || "";
      }
    } catch (error) {
      console.error("Failed to load prefill data", error);
    }
  }

  newClaimItem(): FormGroup {
    return this._formBuilder.group({
      itemDescription: ["", Validators.required],
      datePurchased: [""],
      ownerName: [""],
    });
  }

  addClaimItem(): void {
    this.claimItems.push(this.newClaimItem());
  }

  removeClaimItem(index: number): void {
    this.claimItems.removeAt(index);
  }

  logErrors(): void {
    this.validationService.logValidationErrors(this.dpForm, this.formErrors, this.validationMessages);
  }

  submitForm(): void {
    if (!this.dpForm.valid) {
      this.dpForm.markAllAsTouched();
      return;
    }

    this.initDialogData(
      "Confirm submission",
      "Please proceed only if you have verified that the claim details are correct",
      "Back",
      "Proceed"
    );
    this.showGenericPopup(this.postDataToWhatsapp);
  }

  postDataToWhatsapp = async () => {
    this.loading = true;
    const f = this.dpForm.value;

    const claimItems = (f.claimItems || []).map((item: any) => ({
      ItemDescription: item.itemDescription,
      DatePurchased: item.datePurchased,
      OwnerName: item.ownerName,
    }));

    const t = f.theftDetails || {};
    const req: any = {
      LossDate: f.lossDate,
      LossTime: f.lossTime,
      LossType: f.lossType,
      LossDescription: f.lossDescription,
      ClaimItems: claimItems,
      token: this.token,
      filled: true,
    };

    if (this.showTheftDetails) {
      req.TheftDetails = {
        DiscoveryDate: t.discoveryDate,
        DiscoveryTime: t.discoveryTime,
        DiscoveredBy: t.discoveredBy,
        LastSeenDate: t.lastSeenDate,
        LastSeenTime: t.lastSeenTime,
        LastSeenBy: t.lastSeenBy,
        PoliceNotifiedDate: t.policeNotifiedDate,
        PoliceStationAddress: t.policeStationAddress,
        RecoverySteps: t.recoverySteps,
        TotalContentsValue: t.totalContentsValue,
        ExteriorDoorsSecurity: t.exteriorDoorsSecurity,
        InteriorDoorsSecurity: t.interiorDoorsSecurity,
        WindowsSecurity: t.windowsSecurity,
        FurtherAntiTheftMeasures: t.furtherAntiTheftMeasures,
        SecurityGuardOnDuty: t.securityGuardOnDuty,
        SecurityCompanyName: t.securityCompanyName,
        PremisesUnoccupied: t.premisesUnoccupied,
      };
    }

    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        this.loading = false;
        this.claimNo = response.claimNo;
        this.submitted = true;
      } else {
        this.loading = false;
        this.initDialogData(
          "Submission Failed",
          "An error occurred while submitting your claim",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.postDataToWhatsapp);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.initDialogData(
        "Submission Failed",
        "An unexpected error occurred",
        "Cancel",
        "TRY AGAIN"
      );
      this.showGenericPopup(this.postDataToWhatsapp);
    }
  };

  done(): void {
    window.location.href = environment.whatsappRedirectURL;
  }

  confirmCancel(): void {
    this.initDialogData(
      "Warning",
      "Are you sure you want to cancel this claim?",
      "back",
      "Cancel claim"
    );
    this.showGenericPopup(this.cancelApplication);
  }

  cancelApplication = async () => {
    this.loading = true;
    const req = { token: this.token, filled: false };
    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Failed",
          "An error occurred while attempting to cancel",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.cancelApplication);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      window.location.href = environment.whatsappRedirectURL;
    }
  };

  showGenericPopup(runFunction: () => void): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      disableClose: true,
      data: this.genericDialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        runFunction();
      }
    });
  }

  initDialogData(
    title: string,
    content: string,
    cancelButtonText: string,
    confirmButtonText: string
  ): void {
    this.genericDialogData.title = title;
    this.genericDialogData.content = content;
    this.genericDialogData.cancelButtonText = cancelButtonText;
    this.genericDialogData.confirmButtonText = confirmButtonText;
  }
}
